import { IntegrationScope } from '../../../../types/responses/Interoperability';
import { INTERNAL_ENDPOINTS } from './apiCatalog.internal.generated';
import { ApiEndpoint } from './apiTypes';

/**
 * Catálogo dos endpoints do tenant-cross que a aba documenta. Fonte única: a documentação da
 * aba, a coleção do Postman e o "Try it" saem daqui.
 *
 * Duas seções, com modelos de autenticação diferentes — e a diferença importa:
 *
 * - `partner`: a API de integração sob `/integrations`. Autentica com o token de integração
 *   (Bearer) e cobra escopo. É o que se pode compartilhar com um integrador. Escrita **à mão**
 *   aqui, porque é sensível: o exemplo mostra o usuário mascarado, o token nunca entra na
 *   coleção e cada endpoint declara o escopo que o servidor cobra.
 * - `internal`: a API interna do tenant-cross (charge-boxs, tenants, transactions...). É
 *   **gerada** do OpenAPI do backend (apiCatalog.internal.generated.ts, via
 *   scripts/generate-api-catalog.mjs) para não divergir dele. Autentica por identidade de
 *   tenant/sessão, NÃO pelo token de integração — o token de integração leva 401 nela.
 *
 * Segurança: nenhum exemplo carrega credencial de verdade. O body da seção interna vem do
 * schema (tipos), nunca dos exemplos da coleção de origem.
 */

// Re-exporta os tipos para quem já importava daqui.
export type { ApiSection, ApiAuth, ApiParam, ApiEndpoint } from './apiTypes';
export { isMutating } from './apiTypes';

const PARTNER_ENDPOINTS: ApiEndpoint[] = [
  {
    section: 'partner',
    group: 'Integration',
    method: 'GET',
    path: '/integrations/connections',
    summary: 'Contracts this account has, with the aggregates of each partner.',
    auth: 'INTEGRATION_TOKEN',
    scopes: ['READ'],
    responseExample: `[
  {
    "partner_tenant_id": "aaaa0000-0000-4000-8000-000000000001",
    "partner_name": "Edenred Mobility",
    "partner_alias": "edenred",
    "contract_type": "BIDIRECTIONAL",
    "status": "ACTIVE",
    "shared_station_count": 504,
    "received_station_count": 17,
    "last_session_at": "2026-08-18T06:53:18Z"
  }
]`,
  },
  {
    section: 'partner',
    group: 'Integration',
    method: 'GET',
    path: '/integrations/cross-transactions',
    summary:
      'Charges made by users of other tenants on this account stations. The user identification always arrives masked.',
    auth: 'INTEGRATION_TOKEN',
    scopes: ['READ'],
    query: [
      { name: 'from', description: 'Start of the period, local date and time.', example: '2026-08-01T00:00:00' },
      { name: 'to', description: 'End of the period, inclusive.', example: '2026-08-31T23:59:59' },
      { name: 'partnerTenantId', description: 'Filter by origin tenant.' },
      { name: 'page', description: 'Page, starting at 0.', example: '0' },
      { name: 'size', description: 'Page size.', example: '20' },
    ],
    responseExample: `{
  "content": [
    {
      "transaction_id": "aaaa0001-0000-4000-8000-000000000001",
      "charge_box_code": "MOVE_SC_BMN01",
      "origin_tenant_name": "Edenred Mobility",
      "masked_user": "Tag •••• 4821",
      "start_timestamp": "2026-08-17T15:17:00Z",
      "energy_kwh": 18.7,
      "income": 26.18,
      "payment_status": "APPROVED"
    }
  ],
  "totalElements": 15,
  "totalPages": 1
}`,
  },
];

export const API_ENDPOINTS: ApiEndpoint[] = [...PARTNER_ENDPOINTS, ...INTERNAL_ENDPOINTS];

/** Endpoints da API de parceiro (token de integração). */
export const partnerEndpoints = (): ApiEndpoint[] =>
  API_ENDPOINTS.filter((endpoint) => endpoint.section === 'partner');

/** Endpoints da API interna, agrupados por pasta, na ordem em que aparecem no catálogo. */
export const internalGroups = (): { group: string; endpoints: ApiEndpoint[] }[] => {
  const order: string[] = [];
  const byGroup = new Map<string, ApiEndpoint[]>();

  API_ENDPOINTS.filter((endpoint) => endpoint.section === 'internal').forEach((endpoint) => {
    if (!byGroup.has(endpoint.group)) {
      byGroup.set(endpoint.group, []);
      order.push(endpoint.group);
    }
    byGroup.get(endpoint.group)!.push(endpoint);
  });

  return order.map((group) => ({ group, endpoints: byGroup.get(group)! }));
};

/** Todos os escopos que a API de parceiro exige, sem repetir. */
export const scopesUsedByApi = (): IntegrationScope[] =>
  Array.from(
    new Set(
      API_ENDPOINTS.filter((endpoint) => endpoint.auth === 'INTEGRATION_TOKEN').flatMap(
        (endpoint) => endpoint.scopes ?? [],
      ),
    ),
  );
